import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DateTimePicker } from "@mui/x-date-pickers/DateTimePicker";
import { useEffect, useRef, useState } from "react";
import IStore from "../../models/IStore";
import GenericInputPage from "./GenericInputPage";
import {
  Box,
  Button,
  CircularProgress,
  InputAdornment,
  MenuItem,
  Select,
  Typography,
} from "@mui/material";
import { TextFieldWithIcon, TextFieldWithoutIcon } from "../GenericTextInput";
import { getStores } from "../../api/StoresApi";
import {
  createManualTransaction,
  addManualCards,
  getTicketPrice,
  syncTransactionsForPhone,
} from "../../api/Signature";
import moment from "moment-timezone";
import { IGeneralProps } from "../../models/IGeneralProps";
import { SnackbarType } from "../../enums/SnackbarType";
import IManualTransactionForm, {
  TransactionType,
} from "../../models/IManualTransactionForm";
import axios from "axios";
import UnAuthorizedPage from "./UnAuthorizedPage";
import catchFunction from "../utils/CatchFunction";
import {
  getPhoneErrorAfterChange,
  isValidIsraeliMobilePhone,
} from "../utils/isValidIsraeliMobilePhone";

const loyaltyOptions = [
  {
    id: 1,
    name: "כן",
    value: true,
  },
  {
    id: 2,
    name: "לא",
    value: false,
  },
];

const transactionTypeOptions: { id: number; name: string; value: TransactionType }[] = [
  { id: 1, name: "בחנות", value: "in_store" },
  { id: 2, name: "אונליין", value: "online" },
];

type TicketPriceStatus = "loading" | "ready" | "error";

function isValidMinTicketPrice(price: unknown): price is number {
  return typeof price === "number" && !isNaN(price) && price > 0;
}

function isPositiveInteger(value: string): boolean {
  return /^\d+$/.test(value);
}

export default function AddManuallyPage(props: IGeneralProps) {
  const [isTotalError, setIsTotalError] = useState<boolean>(false);
  const [isTransactionNumberError, setIsTransactionNumberError] =
    useState<boolean>(false);
  const [isCustomerPhoneError, setIsCustomerPhoneError] =
    useState<boolean>(false);
  const [isDateError, setIsDateError] = useState<boolean>(false);
  const [isStoreError, setIsStoreError] = useState<boolean>(false);
  const [selectedStore, setSelectedStore] = useState<IStore | null>(null);
  const [selectedLoyalty, setSelectedLoyalty] = useState(loyaltyOptions[0]);
  const [selectedTransactionType, setSelectedTransactionType] = useState(
    transactionTypeOptions[0]
  );
  const [total, setTotal] = useState<number | string>("");
  const [customerPhone, setCustomerPhone] = useState<string>("");
  const [transactionNumber, setTransactionNumber] = useState<string>("");
  const [receiptNumber, setReceiptNumber] = useState<string>("");
  const [date, setDate] = useState<Date | null>(null);
  const [stores, setStores] = useState<IStore[]>([]);
  const [syncPhone, setSyncPhone] = useState<string>("");
  const [isSyncPhoneError, setIsSyncPhoneError] = useState<boolean>(false);
  const [isSyncing, setIsSyncing] = useState<boolean>(false);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [minTicketPrice, setMinTicketPrice] = useState<number | null>(null);
  const [ticketPriceStatus, setTicketPriceStatus] =
    useState<TicketPriceStatus>("loading");
  const submitInProgressRef = useRef(false);
  const cardsSubmitInProgressRef = useRef(false);
  const [cardsReceiptNumber, setCardsReceiptNumber] = useState<string>("");
  const [cardsCount, setCardsCount] = useState<string>("");
  const [cardsReason, setCardsReason] = useState<string>("");
  const [cardsAddedByText, setCardsAddedByText] = useState<string>(
    props.user?.userName ?? ""
  );
  const [isCardsReceiptNumberError, setIsCardsReceiptNumberError] =
    useState<boolean>(false);
  const [isCardsCountError, setIsCardsCountError] = useState<boolean>(false);
  const [isCardsReasonError, setIsCardsReasonError] = useState<boolean>(false);
  const [isCardsAddedByTextError, setIsCardsAddedByTextError] =
    useState<boolean>(false);
  const [isCardsSubmitting, setIsCardsSubmitting] = useState<boolean>(false);

  function isPositiveIntegerAtLeastOne(value: string): boolean {
    return isPositiveInteger(value) && Number(value) >= 1;
  }

  function clearCardsErrors() {
    setIsCardsReceiptNumberError(false);
    setIsCardsCountError(false);
    setIsCardsReasonError(false);
    setIsCardsAddedByTextError(false);
  }

  function clearCardsForm() {
    clearCardsErrors();
    setCardsReceiptNumber("");
    setCardsCount("");
    setCardsReason("");
    setCardsAddedByText(props.user?.userName ?? "");
  }

  function validateCardsForm(): boolean {
    clearCardsErrors();
    let hasError = false;

    if (!cardsReceiptNumber.trim()) {
      setIsCardsReceiptNumberError(true);
      hasError = true;
    }
    if (!isPositiveIntegerAtLeastOne(cardsCount)) {
      setIsCardsCountError(true);
      hasError = true;
    }
    if (!cardsReason.trim()) {
      setIsCardsReasonError(true);
      hasError = true;
    }
    if (!cardsAddedByText.trim()) {
      setIsCardsAddedByTextError(true);
      hasError = true;
    }

    return !hasError;
  }

  async function handleAddManualCards() {
    if (cardsSubmitInProgressRef.current) {
      return;
    }

    if (!validateCardsForm()) {
      props.setSnackbar({
        open: true,
        type: SnackbarType.error,
        message: "הוזנו נתונים לא חוקיים",
      });
      return;
    }

    cardsSubmitInProgressRef.current = true;
    setIsCardsSubmitting(true);
    try {
      const response = await addManualCards({
        receiptNumber: cardsReceiptNumber.trim(),
        count: Number(cardsCount),
        reason: cardsReason.trim(),
        addedByText: cardsAddedByText.trim(),
      });
      const cardNumbers = response.data.cardNumbers.join(", ");
      props.setSnackbar({
        open: true,
        type: SnackbarType.success,
        message: `נוצרו כרטיסים: ${cardNumbers}`,
      });
      clearCardsForm();
    } catch (error: any) {
      if (error?.response?.status === 404) {
        const serverMessage: string = error?.response?.data?.message ?? "";
        const message = serverMessage.includes("Multiple transactions")
          ? "נמצאו מספר עסקאות עם אותו מספר קבלה — פנה למנהל"
          : "עסקה לא נמצאה למספר קבלה זה";
        props.setSnackbar({
          open: true,
          type: SnackbarType.error,
          message,
        });
      } else {
        catchFunction({ setSnackbar: props.setSnackbar, error });
      }
    } finally {
      cardsSubmitInProgressRef.current = false;
      setIsCardsSubmitting(false);
    }
  }

  function isNumbersOrEmpty(e: any) {
    return !isNaN(e.target.value) || e.target.value === "";
  }

  function clearErrors() {
    setIsTotalError(false);
    setIsTransactionNumberError(false);
    setIsCustomerPhoneError(false);
    setIsDateError(false);
    setIsStoreError(false);
  }

  function clearForm() {
    clearErrors();
    setSelectedStore(null);
    setSelectedLoyalty(loyaltyOptions[0]);
    setSelectedTransactionType(transactionTypeOptions[0]);
    setTotal("");
    setCustomerPhone("");
    setTransactionNumber("");
    setReceiptNumber("");
    setDate(null);
  }

  function validateForm(): boolean {
    clearErrors();
    let hasError = false;

    if (!isValidIsraeliMobilePhone(customerPhone)) {
      setIsCustomerPhoneError(true);
      hasError = true;
    }
    if (!transactionNumber || !isPositiveInteger(transactionNumber)) {
      setIsTransactionNumberError(true);
      hasError = true;
    }
    if (!selectedStore) {
      setIsStoreError(true);
      hasError = true;
    }
    if (
      ticketPriceStatus !== "ready" ||
      minTicketPrice === null ||
      !total ||
      total === "" ||
      isNaN(Number(total)) ||
      Number(total) < minTicketPrice
    ) {
      setIsTotalError(true);
      hasError = true;
    }
    if (moment(date).isAfter(new Date(Date.now())) || date === null) {
      setIsDateError(true);
      hasError = true;
    }

    return !hasError;
  }

  async function handleSyncTransactions() {
    if (!isValidIsraeliMobilePhone(syncPhone)) {
      setIsSyncPhoneError(true);
      return;
    }

    setIsSyncing(true);
    try {
      await syncTransactionsForPhone(syncPhone);
      props.setSnackbar({
        open: true,
        type: SnackbarType.success,
        message: "הסנכרון הושלם בהצלחה",
      });
      setSyncPhone("");
      setIsSyncPhoneError(false);
    } catch (error: any) {
      catchFunction({ setSnackbar: props.setSnackbar, error });
    } finally {
      setIsSyncing(false);
    }
  }

  async function handleSubmit() {
    if (submitInProgressRef.current) {
      return;
    }

    if (!validateForm()) {
      props.setSnackbar({
        open: true,
        type: SnackbarType.error,
        message: "הוזנו נתונים לא חוקיים",
      });
      return;
    }

    const data: IManualTransactionForm = {
      phone: customerPhone,
      number: transactionNumber,
      store: selectedStore!.name,
      total: total.toString(),
      orderDate: date!.toISOString(),
      type: selectedTransactionType.value,
      isLoyalty: selectedLoyalty.value,
      ...(receiptNumber ? { receiptNumber } : {}),
    };

    submitInProgressRef.current = true;
    setIsSubmitting(true);
    try {
      const response = await createManualTransaction(data);
      const result = response.data;

      if (result.ok && result.persisted) {
        props.setSnackbar({
          open: true,
          type: SnackbarType.success,
          message: "העסקה נשמרה והלקוח עודכן בהצלחה",
        });
        clearForm();
      } else if (
        !result.persisted &&
        "skipReason" in result &&
        result.skipReason === "club_membership_required"
      ) {
        props.setSnackbar({
          open: true,
          type: SnackbarType.warning,
          message:
            "העסקה לא נשמרה — נדרשת חברות במועדון לעסקאות שאינן חבר מועדון",
        });
      } else {
        props.setSnackbar({
          open: true,
          type: SnackbarType.error,
          message: "התקבלה תגובה לא צפויה מהשרת",
        });
      }
    } catch (error: any) {
      catchFunction({ setSnackbar: props.setSnackbar, error });
    } finally {
      submitInProgressRef.current = false;
      setIsSubmitting(false);
    }
  }

  useEffect(() => {
    if (axios.defaults.headers.common["Authorization"]) {
      getAndSetStores();
      getAndSetTicketPrice();
    } else {
      setTimeout(() => {
        getAndSetStores();
        getAndSetTicketPrice();
      }, 2000);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (props.user?.userName && !cardsAddedByText) {
      setCardsAddedByText(props.user.userName);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.user?.userName]);

  async function getAndSetStores() {
    getStores()
      .then((response: any) => setStores(response ? response.data : []))
      .catch((error: any) =>
        catchFunction({ setSnackbar: props.setSnackbar, error: error })
      );
  }

  async function getAndSetTicketPrice() {
    setTicketPriceStatus("loading");
    getTicketPrice()
      .then((response) => {
        const price = response.data.ticketPrice;
        if (isValidMinTicketPrice(price)) {
          setMinTicketPrice(price);
          setTicketPriceStatus("ready");
        } else {
          setMinTicketPrice(null);
          setTicketPriceStatus("error");
        }
      })
      .catch((error: any) => {
        setMinTicketPrice(null);
        setTicketPriceStatus("error");
        catchFunction({ setSnackbar: props.setSnackbar, error });
      });
  }

  const totalHelperText =
    ticketPriceStatus === "loading"
      ? "טוען מחיר מינימום..."
      : ticketPriceStatus === "ready" && minTicketPrice !== null
        ? `${minTicketPrice} ש"ח ומעלה`
        : "לא ניתן לטעון מחיר מינימום — נסה שוב";

  return (
    <>
      {!props.user?.helpDeskPermission ? (
        <UnAuthorizedPage />
      ) : (
        <>
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <GenericInputPage
              title={"הקלדת חשבונית"}
              generalProps={props}
              aboveBox={
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    gap: 1.5,
                    flexWrap: "wrap",
                    maxWidth: "100%",
                    minWidth: 0,
                  }}
                >
                  <Typography
                    sx={{
                      fontSize: "18px",
                      fontWeight: "bold",
                      fontFamily: "Noto Sans Hebrew, sans-serif",
                      color: "info.dark",
                    }}
                  >
                    סנכרון עסקאות
                  </Typography>
                  <Box sx={{ display: "flex", gap: 2, alignItems: "flex-start" }}>
                    <TextFieldWithoutIcon
                      fullWidth={true}
                      label="טלפון נייד"
                      variant="outlined"
                      color="info"
                      value={syncPhone}
                      error={isSyncPhoneError}
                      inputProps={{ maxLength: 10 }}
                      onInput={(e: any) => {
                        e.target.value = isNumbersOrEmpty(e)
                          ? e.target.value
                          : syncPhone;
                      }}
                      onChange={(event: any) => {
                        const value = event.target.value;
                        setSyncPhone(value);
                        const nextError = getPhoneErrorAfterChange(value);
                        if (nextError !== null) {
                          setIsSyncPhoneError(nextError);
                        }
                      }}
                      onBlur={(event: any) => {
                        const value = event.target.value;
                        if (value.length > 0) {
                          setIsSyncPhoneError(!isValidIsraeliMobilePhone(value));
                        }
                      }}
                      helperText={
                        isSyncPhoneError
                          ? "יש להזין מספר טלפון נייד תקין (050-055, 057, 058)"
                          : "10 ספרות, מתחיל ב-05"
                      }
                    />
                    <Button
                      variant="contained"
                      color="primary"
                      size="large"
                      key={isSyncing ? "syncing" : "not-syncing"}
                      disabled={!isValidIsraeliMobilePhone(syncPhone) || isSyncing}
                      sx={{
                        fontSize: "16px",
                        fontWeight: "bold",
                        whiteSpace: "nowrap",
                        minWidth: "130px",
                        height: "56px",
                      }}
                      onClick={handleSyncTransactions}
                    >
                      {isSyncing ? (
                        <CircularProgress size={20} color="inherit" />
                      ) : (
                        "סנכרן עסקאות"
                      )}
                    </Button>
                  </Box>
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      gap: 1.5,
                      flexWrap: "wrap",
                      maxWidth: "100%",
                      minWidth: 0,
                      mt: 3,
                      mb: 3,
                      pt: 3,
                      pb: 3,
                      borderTop: 1,
                      borderBottom: 1,
                      borderColor: "divider",
                    }}
                  >
                    <Typography
                      sx={{
                        fontSize: "18px",
                        fontWeight: "bold",
                        fontFamily: "Noto Sans Hebrew, sans-serif",
                        color: "info.dark",
                      }}
                    >
                      הוספת כרטיסים ידנית
                    </Typography>
                    <Box sx={{ display: "flex", width: "100%", gap: 2 }}>
                      <TextFieldWithoutIcon
                        fullWidth={true}
                        label="מספר קבלה"
                        variant="outlined"
                        color="info"
                        value={cardsReceiptNumber}
                        error={isCardsReceiptNumberError}
                        required={true}
                        onChange={(event: any) => {
                          setCardsReceiptNumber(event.target.value);
                          if (isCardsReceiptNumberError) {
                            setIsCardsReceiptNumberError(false);
                          }
                        }}
                        helperText="מספר הקבלה של העסקה"
                      />
                      <TextFieldWithoutIcon
                        fullWidth={true}
                        label="מספר כרטיסים"
                        variant="outlined"
                        color="info"
                        value={cardsCount}
                        error={isCardsCountError}
                        required={true}
                        inputProps={{ min: 1 }}
                        onInput={(e: any) => {
                          e.target.value = /^\d*$/.test(e.target.value)
                            ? e.target.value
                            : cardsCount;
                        }}
                        onChange={(event: any) => {
                          setCardsCount(event.target.value);
                          if (isCardsCountError) {
                            setIsCardsCountError(false);
                          }
                        }}
                        helperText="מספר שלם, לפחות 1"
                      />
                    </Box>
                    <TextFieldWithoutIcon
                      fullWidth={true}
                      label="סיבה"
                      variant="outlined"
                      color="info"
                      value={cardsReason}
                      error={isCardsReasonError}
                      required={true}
                      multiline={true}
                      minRows={3}
                      onChange={(event: any) => {
                        setCardsReason(event.target.value);
                        if (isCardsReasonError) {
                          setIsCardsReasonError(false);
                        }
                      }}
                      helperText="סיבת ההוספה הידנית (למעקב ביקורת)"
                    />
                    <TextFieldWithoutIcon
                      fullWidth={true}
                      label="שם המוסיף"
                      variant="outlined"
                      color="info"
                      value={cardsAddedByText}
                      error={isCardsAddedByTextError}
                      required={true}
                      onChange={(event: any) => {
                        setCardsAddedByText(event.target.value);
                        if (isCardsAddedByTextError) {
                          setIsCardsAddedByTextError(false);
                        }
                      }}
                      helperText="שם העובד שביצע את הפעולה"
                    />
                    <Button
                      variant="contained"
                      color="primary"
                      size="large"
                      key={isCardsSubmitting ? "cards-submitting" : "cards-not-submitting"}
                      disabled={isCardsSubmitting}
                      sx={{
                        fontSize: "16px",
                        fontWeight: "bold",
                        alignSelf: "flex-start",
                        minWidth: "180px",
                      }}
                      onClick={handleAddManualCards}
                    >
                      {isCardsSubmitting ? (
                        <CircularProgress size={20} color="inherit" />
                      ) : (
                        "הוסף כרטיסים"
                      )}
                    </Button>
                  </Box>
                </Box>
              }
              body={
                <>
                  <div style={{ display: "flex", width: "100%", gap: "20px" }}>
                    <div style={{ width: "50%" }}>
                      <TextFieldWithoutIcon
                        fullWidth={true}
                        label="טלפון נייד"
                        variant="outlined"
                        color="info"
                        value={customerPhone}
                        error={isCustomerPhoneError}
                        required={true}
                        inputProps={{ maxLength: 10 }}
                        onInput={(e: any) => {
                          e.target.value = isNumbersOrEmpty(e)
                            ? e.target.value
                            : customerPhone;
                        }}
                        onChange={(event: any) => {
                          const value = event.target.value;
                          setCustomerPhone(value);
                          const nextError = getPhoneErrorAfterChange(value);
                          if (nextError !== null) {
                            setIsCustomerPhoneError(nextError);
                          }
                        }}
                        onBlur={(event: any) => {
                          const value = event.target.value;
                          if (value.length > 0) {
                            setIsCustomerPhoneError(!isValidIsraeliMobilePhone(value));
                          }
                        }}
                        helperText={
                          isCustomerPhoneError
                            ? "יש להזין מספר טלפון נייד תקין (050-055, 057, 058)"
                            : "10 ספרות, מתחיל ב-05"
                        }
                      />
                    </div>
                    <div style={{ width: "50%" }}>
                      <TextFieldWithoutIcon
                        fullWidth={true}
                        label="מספר עסקה"
                        variant="outlined"
                        color="info"
                        value={transactionNumber}
                        error={isTransactionNumberError}
                        required={true}
                        inputProps={{ maxLength: 15 }}
                        onInput={(e: any) => {
                          e.target.value = /^\d*$/.test(e.target.value)
                            ? e.target.value
                            : transactionNumber;
                        }}
                        onChange={(event: any) => {
                          setTransactionNumber(event.target.value);
                          if (isTransactionNumberError)
                            setIsTransactionNumberError(false);
                        }}
                        helperText="מספר שלם"
                      />
                    </div>
                  </div>
                  <div style={{ display: "flex", width: "100%", gap: "20px" }}>
                    <div style={{ width: "50%" }}>
                      <TextFieldWithIcon
                        label="סכום חשבונית"
                        type="number"
                        error={isTotalError}
                        onChange={(event: any) => {
                          setTotal(event.target.value);
                          if (isTotalError) setIsTotalError(false);
                        }}
                        variant="outlined"
                        color="info"
                        value={total}
                        required={true}
                        startIconComponent={
                          <InputAdornment position="start">₪</InputAdornment>
                        }
                        iconPosition="start"
                        helperText={totalHelperText}
                      />
                      {ticketPriceStatus === "error" && (
                        <Button
                          size="small"
                          variant="text"
                          color="primary"
                          onClick={getAndSetTicketPrice}
                          sx={{ mt: 0.5 }}
                        >
                          נסה שוב לטעון מחיר מינימום
                        </Button>
                      )}
                    </div>
                    <div style={{ width: "50%" }}>
                      <TextFieldWithoutIcon
                        fullWidth={true}
                        label="מספר קבלה"
                        variant="outlined"
                        color="info"
                        value={receiptNumber}
                        onChange={(event: any) =>
                          setReceiptNumber(event.target.value)
                        }
                        helperText="אופציונלי"
                      />
                    </div>
                  </div>
                  <div style={{ display: "flex", width: "100%", gap: "20px" }}>
                    <div style={{ width: "50%" }}>
                      <TextFieldWithIcon
                        variant="outlined"
                        fullWidth={true}
                        color="info"
                        label="סניף"
                        error={isStoreError}
                        value={selectedStore}
                        required={true}
                        select={
                          <Select
                            variant="outlined"
                            color="primary"
                            error={isStoreError}
                            label="סניף"
                            labelId="store-select-label"
                            id="store-select"
                            value={selectedStore}
                            sx={{ fontSize: "18px", textAlign: "start" }}
                            onChange={(event: any) => {
                              setSelectedStore(event.target.value);
                              if (isStoreError) setIsStoreError(false);
                            }}
                          >
                            {stores.map((store) => (
                              // @ts-ignore - necessary to load object into value
                              <MenuItem
                                sx={{
                                  justifyContent: "end",
                                  fontFamily: "Noto Sans Hebrew, sans-serif",
                                  fontSize: "18px",
                                }}
                                value={store}
                                key={store.id}
                              >
                                {store.name}
                              </MenuItem>
                            ))}
                          </Select>
                        }
                      />
                    </div>
                    <div style={{ width: "50%" }}>
                      <TextFieldWithIcon
                        variant="outlined"
                        fullWidth={true}
                        color="info"
                        label="סוג עסקה"
                        value={selectedTransactionType}
                        required={true}
                        select={
                          <Select
                            variant="outlined"
                            color="primary"
                            label="סוג עסקה"
                            labelId="transaction-type-select-label"
                            id="transaction-type-select"
                            value={selectedTransactionType}
                            sx={{ fontSize: "18px", textAlign: "start" }}
                            onChange={(event: any) =>
                              setSelectedTransactionType(event.target.value)
                            }
                          >
                            {transactionTypeOptions.map((option) => (
                              // @ts-ignore - necessary to load object into value
                              <MenuItem
                                sx={{
                                  justifyContent: "end",
                                  fontFamily: "Noto Sans Hebrew, sans-serif",
                                  fontSize: "18px",
                                }}
                                value={option}
                                key={option.id}
                              >
                                {option.name}
                              </MenuItem>
                            ))}
                          </Select>
                        }
                      />
                    </div>
                  </div>
                  <div style={{ display: "flex", width: "100%", gap: "20px" }}>
                    <div style={{ width: "50%" }}>
                      <TextFieldWithIcon
                        variant="outlined"
                        fullWidth={true}
                        color="info"
                        label="חבר מועדון"
                        value={selectedLoyalty}
                        required={true}
                        select={
                          <Select
                            variant="outlined"
                            color="primary"
                            label="חבר מועדון"
                            labelId="loyalty-select-label"
                            id="loyalty-select"
                            value={selectedLoyalty}
                            sx={{ fontSize: "18px", textAlign: "start" }}
                            onChange={(event: any) =>
                              setSelectedLoyalty(event.target.value)
                            }
                          >
                            {loyaltyOptions.map((loyalty) => (
                              // @ts-ignore - necessary to load object into value
                              <MenuItem
                                sx={{
                                  justifyContent: "end",
                                  fontFamily: "Noto Sans Hebrew, sans-serif",
                                  fontSize: "18px",
                                }}
                                value={loyalty}
                                key={loyalty.id}
                              >
                                {loyalty.name}
                              </MenuItem>
                            ))}
                          </Select>
                        }
                      />
                    </div>
                  </div>

                  <TextFieldWithIcon
                    label=""
                    variant="outlined"
                    color="info"
                    select={
                      <DateTimePicker
                        disableFuture
                        slotProps={{
                          textField: {
                            error: isDateError,
                            required: true,
                          },
                        }}
                        ampm={false}
                        format="YYYY-MM-DD-HH:mm"
                        sx={{ direction: "ltr", textAlign: "start" }}
                        label="מועד ביצוע עסקה"
                        value={date}
                        onChange={(value: any) => {
                          setDate(value ? new Date(value) : null);
                          if (isDateError) setIsDateError(false);
                        }}
                      />
                    }
                  />
                  <Button
                    fullWidth={true}
                    variant="contained"
                    color="primary"
                    size="large"
                    key={isSubmitting ? "submitting" : "not-submitting"}
                    disabled={isSubmitting || ticketPriceStatus !== "ready"}
                    sx={{ fontSize: "20px", fontWeight: "bold" }}
                    onClick={handleSubmit}
                  >
                    {isSubmitting ? (
                      <CircularProgress size={24} color="inherit" />
                    ) : (
                      "שמור עסקה ועדכן לקוח"
                    )}
                  </Button>
                </>
              }
            />
          </LocalizationProvider>
        </>
      )}
    </>
  );
}
